import React, { useCallback, useEffect, useState } from "react";
import { Network, Gavel, AlertTriangle, TrendingUp, Flag, Target, ArrowRight, PoundSterling } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const TYPE_META = {
  decision: { label: "Decision", icon: Gavel, cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  risk: { label: "Risk / issue", icon: AlertTriangle, cls: "text-red-700 bg-red-50 border-red-200" },
  gate: { label: "Stage-gate", icon: Flag, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  benefit: { label: "Benefit", icon: TrendingUp, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  gap: { label: "Maturity gap", icon: Target, cls: "text-sky-700 bg-sky-50 border-sky-200" },
};
const tid = (id) => String(id).replace(/[^A-Za-z0-9]+/g, "-");

const Chip = ({ node, active, onClick }) => {
  const m = TYPE_META[node.type] || {};
  const Icon = m.icon || Network;
  return (
    <button data-testid={`eg-node-${tid(node.id)}`} onClick={() => onClick(node)} title={node.label}
      className={`inline-flex items-center gap-1.5 text-left text-[12px] px-2.5 py-1.5 rounded border transition-colors ${active ? "ring-2 ring-[var(--e360-primary)] " : ""}${m.cls || "bg-zinc-50 border-zinc-200 text-zinc-600"}`}>
      <Icon size={12} className="flex-none" />
      <span className="truncate max-w-[220px]">{node.ref ? `${node.ref} · ` : ""}{node.label}</span>
      {node.value_label && <span className="text-emerald-700 font-semibold whitespace-nowrap">· {node.value_label}</span>}
    </button>
  );
};

function Group({ title, nodes, onClick }) {
  if (!nodes || nodes.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400 mb-1.5">{title} ({nodes.length})</div>
      <div className="flex flex-wrap gap-1.5">
        {nodes.map((n) => <Chip key={n.id} node={n} onClick={onClick} />)}
      </div>
    </div>
  );
}

const Stat = ({ label, value, cls = "text-black" }) => (
  <div className="text-center"><div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{label}</div></div>
);

export default function EvidenceGraph({ clientId, apiBase }) {
  const base = apiBase || `/admin/enterprise360/clients/${clientId}`;
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);      // query result for the selected node
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try { const { data } = await api.get(`${base}/evidence-graph`); setGraph(data); }
    catch { toast.error("Could not load the evidence graph"); }
    finally { setLoading(false); }
  }, [clientId, base]);
  useEffect(() => { load(); }, [load]);

  const select = async (node) => {
    setSelId(node.id); setBusy(true);
    try {
      const { data } = await api.post(`${base}/evidence-graph/query`, { node_id: node.id });
      if (data?.error) { toast.error("Node not found"); setSel(null); }
      else setSel(data);
    } catch { toast.error("Query failed"); }
    finally { setBusy(false); }
  };

  const nodes = graph?.nodes || [];
  const byType = (t) => nodes.filter((n) => n.type === t);
  const decisions = byType("decision");
  const risks = byType("risk");
  const impact = sel?.impact || {};
  const prov = sel?.provenance || {};
  const m = sel ? (TYPE_META[sel.node.type] || {}) : {};
  const SelIcon = m.icon || Network;

  return (
    <div data-testid="evidence-graph">
      <p className="text-[12px] text-zinc-500 max-w-2xl mb-3">
        Bidirectional causal traceability across the delivery registers — pick a decision or a risk to see
        every stage-gate, benefit and maturity gap it drives or endangers, and the £ value at stake on that path.
      </p>

      {loading ? <div className="text-sm text-zinc-400 py-6 text-center">Loading evidence graph…</div> : (
        <>
          <div data-testid="eg-summary" className="flex items-center gap-6 flex-wrap border border-zinc-200 rounded-lg px-4 py-3 mb-4">
            <Stat label="Nodes" value={graph?.stats?.nodes ?? nodes.length} />
            <Stat label="Causal links" value={graph?.stats?.edges ?? (graph?.edges || []).length} />
            {graph?.value_at_stake_label && (
              <div className="text-center" data-testid="eg-vas-total">
                <div className="text-2xl font-semibold text-[var(--e360-primary)]">{graph.value_at_stake_label}</div>
                <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">Value at stake</div>
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-[minmax(0,320px)_1fr] gap-4">
            {/* Cause picker */}
            <div className="border border-zinc-200 rounded-lg p-3">
              <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[var(--e360-primary)] mb-2 inline-flex items-center gap-1">
                <Network size={13} /> Trace from
              </div>
              <Group title="Decisions" nodes={decisions} onClick={select} />
              <Group title="Risks &amp; issues" nodes={risks} onClick={select} />
              {decisions.length === 0 && risks.length === 0 && (
                <div className="text-[12px] text-zinc-400">No decisions or risks tracked yet.</div>
              )}
            </div>

            {/* Impact / provenance panel */}
            <div data-testid="eg-detail" className="border border-zinc-200 rounded-lg p-4 min-h-[200px]">
              {!sel ? (
                <div className="text-[13px] text-zinc-400 py-10 text-center">Select a decision or risk on the left to trace its impact.</div>
              ) : (
                <>
                  <div data-testid="eg-selected" className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-semibold ${m.cls}`}>
                      <SelIcon size={12} /> {m.label}
                    </span>
                    <span className="text-[14px] font-medium text-zinc-800">{sel.node.ref ? `${sel.node.ref} · ` : ""}{sel.node.label}</span>
                    {sel.node.status && <span className="text-[11px] text-zinc-400">· {sel.node.status}</span>}
                  </div>

                  {sel.value_at_stake_exposed && (
                    <div data-testid="eg-vas-banner" className="mt-2 mb-3 inline-flex items-center gap-2 text-[12px] text-[var(--e360-primary)] bg-[var(--e360-primary)]/5 border border-[var(--e360-primary)]/30 rounded-md px-3 py-1.5">
                      <PoundSterling size={14} />
                      <span><b>{sel.value_at_stake_exposed}</b> of value at stake sits on this path — {sel.counts.benefits_at_risk} benefit{sel.counts.benefits_at_risk === 1 ? "" : "s"} downstream.</span>
                    </div>
                  )}

                  <div className="grid md:grid-cols-3 gap-3 mb-4 mt-2">
                    <Stat label="Nodes impacted" value={sel.counts.impacted} />
                    <Stat label="Benefits at risk" value={sel.counts.benefits_at_risk} cls={sel.counts.benefits_at_risk ? "text-red-600" : "text-black"} />
                    <Stat label="Gates affected" value={sel.counts.gates_affected} cls={sel.counts.gates_affected ? "text-amber-600" : "text-black"} />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div data-testid="eg-impact">
                      <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-zinc-600 mb-2 inline-flex items-center gap-1">
                        Drives / endangers <ArrowRight size={12} />
                      </div>
                      <Group title="Stage-gates" nodes={impact.gate} onClick={select} />
                      <Group title="Benefits" nodes={impact.benefit} onClick={select} />
                      <Group title="Maturity gaps" nodes={impact.gap} onClick={select} />
                      {!(impact.gate?.length || impact.benefit?.length || impact.gap?.length) && <div className="text-[12px] text-zinc-400">Nothing downstream.</div>}
                    </div>
                    <div data-testid="eg-provenance">
                      <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-zinc-600 mb-2 inline-flex items-center gap-1">
                        <ArrowRight size={12} className="rotate-180" /> Driven by / depends on
                      </div>
                      <Group title="Decisions" nodes={prov.decision} onClick={select} />
                      <Group title="Stage-gates" nodes={prov.gate} onClick={select} />
                      <Group title="Risks &amp; issues" nodes={prov.risk} onClick={select} />
                      {!(prov.decision?.length || prov.gate?.length || prov.risk?.length) && <div className="text-[12px] text-zinc-400">No upstream drivers.</div>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
